import {
  SidebarSearchBranch,
  useSidebarSearchBranch,
  useSidebarSearchCollapsed,
} from '@affine/core/components/sidebar-tree-search';
import { CategoryDivider } from '@affine/core/modules/app-sidebar/views';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import * as Collapsible from '@radix-ui/react-collapsible';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import {
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { SIDEBAR_ATOM_ROW_HEIGHT } from '../../../../modules/app-sidebar/views/sidebar-layout';
import { AnimatedNavigationPanelTreeDisclosureContent } from '../tree/disclosure-content';
import {
  NavigationTreeSectionOwner,
  type TreeNavigationEntry,
} from '../tree/navigation';
import { content, header, root } from './collapsible-section.css';

interface CollapsibleSectionProps extends PropsWithChildren {
  navigationEntries?: readonly TreeNavigationEntry[];
  navigationLabel?: string;
  path: string[];
  title: ReactNode;
  titleInteractive?: boolean;
  actions?: ReactNode;

  className?: string;
  testId?: string;
  rootDataAttributes?: Record<`data-${string}`, string | undefined>;

  headerRef?: RefObject<HTMLDivElement>;
  headerTestId?: string;
  headerClassName?: string;

  contentClassName?: string;
  contentStyle?: CSSProperties;
  revealOnExpand?: boolean;
  revealOnMount?: boolean;
}

const MIN_REVEAL_SCROLL_DELTA = SIDEBAR_ATOM_ROW_HEIGHT / 2;

function findSidebarScrollViewport(element: HTMLElement) {
  return element.closest<HTMLElement>('[data-sidebar-scroll-viewport="true"]');
}

function scrollExpandedSectionIntoView(root: HTMLElement) {
  const viewport = findSidebarScrollViewport(root);
  const header = root.querySelector<HTMLElement>(
    '[data-navigation-panel-section-header="true"]'
  );
  if (!viewport || !header) return;

  const viewportRect = viewport.getBoundingClientRect();
  const headerRect = header.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  const maxScrollTop = Math.max(
    0,
    viewport.scrollHeight - viewport.clientHeight
  );

  const scrollToRevealTarget = (targetScrollTop: number) => {
    const clampedTargetScrollTop = Math.min(
      maxScrollTop,
      Math.max(0, targetScrollTop)
    );
    const rawDelta = clampedTargetScrollTop - viewport.scrollTop;
    if (Math.abs(rawDelta) < MIN_REVEAL_SCROLL_DELTA) return;

    const atomDelta =
      Math.sign(rawDelta) *
      Math.round(Math.abs(rawDelta) / SIDEBAR_ATOM_ROW_HEIGHT) *
      SIDEBAR_ATOM_ROW_HEIGHT;
    if (atomDelta === 0) return;

    const nextScrollTop = Math.min(
      maxScrollTop,
      Math.max(0, viewport.scrollTop + atomDelta)
    );
    if (
      Math.abs(nextScrollTop - viewport.scrollTop) < MIN_REVEAL_SCROLL_DELTA
    ) {
      return;
    }

    streamLainAnimationController.scrollTo(viewport, {
      top: nextScrollTop,
    });
  };

  if (headerRect.top < viewportRect.top) {
    scrollToRevealTarget(
      viewport.scrollTop + headerRect.top - viewportRect.top
    );
    return;
  }

  if (headerRect.bottom > viewportRect.bottom) {
    scrollToRevealTarget(
      viewport.scrollTop + headerRect.bottom - viewportRect.bottom
    );
    return;
  }

  const overflowBottom = rootRect.bottom - viewportRect.bottom;
  if (overflowBottom <= 0) return;

  const headerTopRoom = Math.max(0, headerRect.top - viewportRect.top);
  const scrollDelta = Math.min(overflowBottom, headerTopRoom);
  if (scrollDelta <= 0) return;

  scrollToRevealTarget(viewport.scrollTop + scrollDelta);
}

function scrollCreatedSectionToTop(root: HTMLElement) {
  const viewport = findSidebarScrollViewport(root);
  const header = root.querySelector<HTMLElement>(
    '[data-navigation-panel-section-header="true"]'
  );
  if (!viewport || !header) return;

  const viewportRect = viewport.getBoundingClientRect();
  const headerRect = header.getBoundingClientRect();
  const maxScrollTop = Math.max(
    0,
    viewport.scrollHeight - viewport.clientHeight
  );
  streamLainAnimationController.scrollTo(viewport, {
    top: Math.min(
      maxScrollTop,
      Math.max(0, viewport.scrollTop + headerRect.top - viewportRect.top)
    ),
  });
}

export const CollapsibleSection = ({
  navigationEntries,
  navigationLabel,
  path,
  title,
  titleInteractive = false,
  actions,
  children,

  className,
  testId,
  rootDataAttributes,

  headerRef,
  headerTestId,
  headerClassName,

  contentClassName,
  contentStyle,
  revealOnExpand = false,
  revealOnMount = false,
}: CollapsibleSectionProps) => {
  const navigationPanelService = useService(NavigationPanelService);

  const storedCollapsed = useLiveData(navigationPanelService.collapsed$(path));
  const collapsed = useSidebarSearchCollapsed(storedCollapsed, path);
  const search = useSidebarSearchBranch(title);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const localHeaderRef = useRef<HTMLDivElement | null>(null);
  const actualHeaderRef = headerRef ?? localHeaderRef;

  const setCollapsed = useCallback(
    (v: boolean) => {
      if (!search.active) navigationPanelService.setCollapsed(path, v);
    },
    [navigationPanelService, path, search.active]
  );

  const handleExpansionSettled = useCallback(
    (expanded: boolean) => {
      if (expanded && !search.active && revealOnExpand && rootRef.current) {
        scrollExpandedSectionIntoView(rootRef.current);
      }
    },
    [revealOnExpand, search.active]
  );
  const expand = useCallback(() => setCollapsed(false), [setCollapsed]);

  useEffect(() => {
    if (revealOnMount && rootRef.current)
      scrollCreatedSectionToTop(rootRef.current);
  }, [revealOnMount]);

  const section = (
    <SidebarSearchBranch id={search.id}>
      <Collapsible.Root
        {...rootDataAttributes}
        data-collapsed={collapsed}
        className={clsx(root, className)}
        open={!collapsed}
        ref={rootRef}
        data-testid={testId}
        data-sidebar-search-hidden={!search.visible || undefined}
      >
        <CategoryDivider
          data-testid={headerTestId}
          data-navigation-panel-section-header="true"
          label={title}
          interactiveLabel={titleInteractive}
          setCollapsed={setCollapsed}
          collapsed={collapsed}
          animate={search.animate}
          ref={actualHeaderRef}
          className={clsx(header, headerClassName)}
        >
          {actions}
        </CategoryDivider>
        <AnimatedNavigationPanelTreeDisclosureContent
          collapsed={collapsed}
          animate={search.animate}
          onSettled={handleExpansionSettled}
          data-testid="collapsible-section-content"
          className={clsx(content, contentClassName)}
          style={contentStyle}
        >
          {children}
        </AnimatedNavigationPanelTreeDisclosureContent>
      </Collapsible.Root>
    </SidebarSearchBranch>
  );
  const label =
    navigationLabel ?? (typeof title === 'string' ? title : undefined);
  return navigationEntries && label ? (
    <NavigationTreeSectionOwner
      path={path}
      label={label}
      entries={navigationEntries}
      headerRef={actualHeaderRef}
      onExpand={expand}
    >
      {section}
    </NavigationTreeSectionOwner>
  ) : (
    section
  );
};
